import {useEffect, useMemo, useState} from 'react';
import {Box, DialogActions, Typography} from '@mui/material';
import {t} from 'i18next';
import {v4 as uuidV4} from 'uuid';
import {GridColDef, GridSortModel} from '@mui/x-data-grid';
import {
  useExportIndexingErrorData,
  useIndexErrorsGridData,
} from '../../../../hooks/ingestions/useIngestions';
import ArrModal from '../../../common/arr-modal/ArrModal';
import ArrDataGrid from '../../../common/arr-data-grid/ArrDataGrid';
import {ReactComponent as InfoIcon} from '../../../../assets/images/info-icon.svg';
import {ReactComponent as ExportIcon} from '../../../../assets/images/icon-report-export.svg';
import './ESIndexErrorListModal.scss';
import ArrPagination from '../../../common/arr-pagination/ArrPagination';
import ArrButton from '../../../common/arr-button/ArrButton';
import {PAGE_SIZE, ToastType} from '../../../../shared/constants';
import {
  IndexErrorsGridItemType,
  ToastMessageConfigType,
} from '../../../../api/types/ingestionApi.type';
import CustomNoRowsOverlay from '../CustomNoRowsOverlay/CustomNoRowsOverlay';
import ArrTooltipWrapper from '../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';

interface Props {
  ingestionId: number;
  showModal: boolean;
  onClose: () => void;
  showToast: (config: ToastMessageConfigType) => void;
  showExportSuccessToast: (config: ToastMessageConfigType) => void;
}

const defaultSortColumn = {
  field: 'fileName',
  sort: 'desc',
};

interface ErrorListDataType extends IndexErrorsGridItemType {
  id: string;
}
const stringPath = 'ingestions.esIndexing.errorModal.';
const toastStringPath = 'ingestions.esIndexing.toastMsg.';

const NoRowsOverlay = () => {
  return (
    <CustomNoRowsOverlay
      className="no-row-container"
      message={t('ingestions.esIndexing.noQueryReturnDataMsg')}
    />
  );
};

const ESIndexErrorListModal: React.FC<Props> = ({
  ingestionId,
  showModal,
  onClose,
  showToast,
  showExportSuccessToast,
}) => {
  const [pageNumber, setPageNumber] = useState(1);
  const [itemPerPage, setItemPerPage] = useState(PAGE_SIZE);
  const [errorListData, setErrorListData] = useState<ErrorListDataType[]>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    defaultSortColumn,
  ] as GridSortModel);

  const {
    data: indexErrorGridData,
    isLoading: isIndexErrorGridLoading,
    error: indexGridError,
    isError: isIndexGridError,
  } = useIndexErrorsGridData({
    pageNumber: pageNumber,
    pageSize: itemPerPage,
    sortingColumn: sortModel[0]?.field ?? defaultSortColumn.field,
    direction: sortModel[0]?.sort ?? defaultSortColumn.sort,
    ingestionId: ingestionId,
  });

  const {
    isLoading: isExportLoading,
    isSuccess: isExportSuccess,
    isError: isExportError,
    error: exportError,
    refetch: sendExportReq,
  } = useExportIndexingErrorData({
    analyticsId: ingestionId,
  });

  useEffect(() => {
    const errorsWithId: ErrorListDataType[] = (
      indexErrorGridData?.data ?? []
    ).map((item) => {
      return {
        ...item,
        id: uuidV4(),
      };
    });
    setErrorListData(errorsWithId);
  }, [indexErrorGridData?.data]);

  // Track export report success and show toast for it
  useEffect(() => {
    if (isExportSuccess && !isExportLoading) {
      showExportSuccessToast({
        title: t(toastStringPath + 'exportInitiatedTitle'),
        message: t(toastStringPath + 'exportSuccess'),
        type: ToastType.Success,
      });
    }
  }, [showExportSuccessToast, isExportSuccess, isExportLoading]);

  // Track export report failed and show toast for it
  useEffect(() => {
    if (!isExportLoading && isExportError && exportError) {
      showToast({
        title: t('common.error'),
        message: t(toastStringPath + 'exportIndexingErrorFailed'),
        type: ToastType.Error,
      });
    }
  }, [showToast, isExportError, exportError, isExportLoading]);

  // Track Load index error grid data faield case and show toast for it
  useEffect(() => {
    if (!isIndexErrorGridLoading && isIndexGridError && indexGridError) {
      showToast({
        title: t('common.error'),
        message: t(toastStringPath + 'indexErrorGridLoadFailed'),
        type: ToastType.Error,
      });
    }
  }, [showToast, isIndexErrorGridLoading, isIndexGridError, indexGridError]);

  const columns = useMemo<GridColDef[]>(() => {
    return [
      {
        field: 'fileName',
        headerName: t(stringPath + 'sourceDocId'),
        flex: 0.33,
      },
      {
        field: 'error',
        headerName: t(stringPath + 'error'),
        flex: 0.67,
        headerClassName: 'last-column',
      },
    ];
  }, [t]);

  const renderFooter = () => {
    return (
      <DialogActions className="footer-action-container" id={`${pageNumber}`}>
        <ArrPagination
          totalItems={indexErrorGridData?.totalRecords ?? 0}
          perPage={itemPerPage}
          currentPage={pageNumber}
          onPerPageChange={setItemPerPage}
          onPageChange={setPageNumber}
          showPerPageOptions
          perPageOptions={[10]}
          mode="limited"
          alwaysShow
          isFetching={isIndexErrorGridLoading}
        />
        <Box display="flex" className="action-button-box">
          <ArrButton
            variant="secondary"
            size="medium"
            onClick={onClose}
            aria-label={t('common.close')}
            dataTestId="esindex-error-list-modal-close-button"
          >
            {t('common.close')}
          </ArrButton>
          <ArrButton
            variant="primary"
            size="medium"
            onClick={() => {
              if (!isExportLoading) sendExportReq();
            }}
            aria-label={t('common.export')}
            dataTestId="export-index-error-button"
            startIcon={<ExportIcon />}
            loading={isExportLoading}
          >
            {t('common.export')}
          </ArrButton>
        </Box>
      </DialogActions>
    );
  };

  return (
    <ArrModal
      showModal={showModal}
      onClose={onClose}
      hideDefaultActions
      className="es-index-error-list-modal"
      dataTestId="es-index-error-list-modal"
      headerTitleElement={
        <Box display="flex" alignItems="center" className="title-container">
          <Typography
            className="typo-text headline3_size heading-title-text"
            tabIndex={0}
            data-testid={`esindex-error-list-modal-indexing-error`}
          >
            {t(stringPath + 'indexingError')}
          </Typography>
          <ArrTooltipWrapper
            title={t(stringPath + 'indexingError')}
            trigger={
              <Box display="flex">
                <InfoIcon className="error-list-info-icon" tabIndex={0} />
              </Box>
            }
          >
            {t(stringPath + 'indexingError')}
          </ArrTooltipWrapper>
        </Box>
      }
      footer={renderFooter()}
      autoFocusCloseButton
    >
      <Box className="error-list-grid-container">
        <ArrDataGrid
          rows={errorListData}
          rowCount={indexErrorGridData?.totalRecords ?? 0}
          columns={columns}
          paginationMode="server"
          primaryKey="id"
          loading={isIndexErrorGridLoading}
          onSortModelChange={setSortModel}
          hideFooter
          hideFooterPagination
          initialState={{
            sorting: {
              sortModel: [defaultSortColumn] as GridSortModel,
            },
          }}
          noRowsOverlay={NoRowsOverlay}
        />
      </Box>
    </ArrModal>
  );
};

export default ESIndexErrorListModal;
